import React, {useState, useEffect} from "react";
import {makeStyles} from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Title from "../dashboard/Title";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import SelectInput from "./input/SelectInput";
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {Redirect, useParams} from "react-router-dom";
import {NotificationManager} from 'react-notifications';
import Paper from "@material-ui/core/Paper";
import TextInput from "./input/TextInput";
import {Grid} from "@material-ui/core";
import Popup from "../presentation/Popup";
import {useDashboardStyles, BRAND} from "../settings/dashboardStyles";

const useLocalStyles = makeStyles((theme) => ({
    formField: {
        marginBottom: theme.spacing(2),
    },
    formActionsBar: {
        marginTop: theme.spacing(3),
        display: "flex",
        gap: theme.spacing(1.5),
        justifyContent: "flex-end",
        flexWrap: "wrap",
        [theme.breakpoints.down("xs")]: {
            "& > button": {
                flex: "1 1 auto",
            },
        },
    },
    warningText: {
        color: "#a8324a",
        fontWeight: 600,
        fontSize: "1rem",
        marginBottom: theme.spacing(1),
    },
    warningSubtitle: {
        color: BRAND.deep,
        fontWeight: 600,
        marginTop: theme.spacing(1.5),
    },
    warningBody: {
        color: BRAND.inkSoft,
        fontSize: "0.92rem",
    },
}));

export default function PlaceForm(props) {
    const classes = useDashboardStyles();
    const localClasses = useLocalStyles();

    const [values, setValues] = useState({
        cremation: '',
        new_monument: '',
        cemetery: '',
        place_number: '',
        depth_hole: '',
        grave: '',
        uncover_grave: '',
        who_is_buried: '',
        period_buried: '',
        covering_cement: '',
        exhumation: '',
        owner: '',
        google_maps_embed_url: '',
        funeral_id: useParams().funeral_id
    });

    const [, setOldValues] = useState({});
    const [show, setShow] = useState(false);
    const [, setSignatures] = useState({});
    const [success, setSuccess] = useState(false);

    const handleChange = (event) => {
        const {name, value} = event.target;
        setValues({...values, [name]: value});
    }

    const params = useParams();
    const hasProperty = params.hasOwnProperty('place_id');

    useEffect(() => {
        UserService.getSignatures(params.funeral_id).then(
            (response) => {
                setSignatures(response.data);
            }
        )
        if (hasProperty) {
            UserService.getPlace(params.place_id).then(
                (response) => {
                    setValues((currentValues) => ({
                        ...currentValues,
                        ...response.data,
                        funeral_id: params.funeral_id
                    }));
                    setOldValues(response.data);
                },
                (e) => {
                    if (e.response.status === 401) {
                        logOut(props);
                        NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                    }
                }
            );
        }
    }, [hasProperty, params.funeral_id, params.place_id, props]);

    const handleCreate = (e) => {
        e.preventDefault();
        UserService.createPlace(values).then(
            () => {
                setSuccess(true);
                NotificationManager.success('Dáta o mieste upravené.', 'Upravené', 5000);
            },
            (e) => {
                if (e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                } else {
                    NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
                }
            }
        );
    };

    const yesNoOptions = [
        {value: 'Áno', title: 'Áno'},
        {value: 'Nie', title: 'Nie'},
    ];

    return (
        <form autoComplete='off' onSubmit={handleCreate}>
            <Container maxWidth="lg" className={classes.pageContainer}>
                <Paper className={classes.paper} elevation={0}>
                    <div className={classes.sectionHeader}>
                        <Title>Úprava miesta</Title>
                    </div>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <SelectInput label="Spopolnenie" name={'cremation'} value={values.cremation} options={yesNoOptions} onChange={handleChange} fullWidth/>
                            </div>
                            <div className={localClasses.formField}>
                                <SelectInput label="Nový hrob" name={'new_monument'} value={values.new_monument} options={yesNoOptions} onChange={handleChange} fullWidth/>
                            </div>
                            <div className={localClasses.formField}>
                                <SelectInput
                                    label="Hĺbka hrobu"
                                    name={'depth_hole'}
                                    value={values.depth_hole}
                                    options={[
                                        {value: '0.7m (potratený/predčasne odňatý ľudský plod)', title: '0.7m (potratený/predčasne odňatý ľudský plod)'},
                                        {value: '1.2m (dieťa do 10r.)', title: '1.2m (dieťa do 10r.)'},
                                        {value: '1.6m (1 osoba)', title: '1.6m (1 osoba)'},
                                        {value: '2.2m (2 osoby)', title: '2.2m (2 osoby)'},
                                        {value: '2.7m (3 osoby)', title: '2.7m (3 osoby)'},
                                    ]}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </div>
                            <div className={localClasses.formField}>
                                <SelectInput label="Hrobka" name={'grave'} value={values.grave} options={yesNoOptions} onChange={handleChange} fullWidth/>
                            </div>
                            <div className={localClasses.formField}>
                                <SelectInput label="Odkryť pomník s krycou doskou" name={'uncover_grave'} value={values.uncover_grave} options={yesNoOptions} onChange={handleChange} fullWidth/>
                            </div>
                            <div className={localClasses.formField}>
                                <SelectInput label="Zaliatie panelov cementovou maltou" name={'covering_cement'} value={values.covering_cement} options={yesNoOptions} onChange={handleChange} fullWidth/>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={localClasses.formField}>
                                <TextInput label="Cintorín" name={'cemetery'} value={values.cemetery} onChange={handleChange} fullWidth/>
                            </div>
                            <div className={localClasses.formField}>
                                <TextInput label="Číslo hrobového miesta" name={'place_number'} value={values.place_number} onChange={handleChange} fullWidth/>
                            </div>
                            <div className={localClasses.formField}>
                                <TextInput
                                    label="Google Maps (odkaz alebo embed)"
                                    placeholder="Zdieľať → Vložiť mapu (alebo krátky odkaz)"
                                    name={'google_maps_embed_url'}
                                    value={values.google_maps_embed_url}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </div>
                            <div className={localClasses.formField}>
                                <TextInput
                                    label="Majiteľ hrobového miesta"
                                    placeholder={"Meno Priezvisko, Adresa, Dátum narodenia, Kontakt"}
                                    name={'owner'}
                                    value={values.owner}
                                    onChange={handleChange}
                                    multiline
                                    rows={2}
                                    fullWidth
                                />
                            </div>
                            <div className={localClasses.formField}>
                                <TextInput label="Kto tu bol pochovaný" name={'who_is_buried'} value={values.who_is_buried} onChange={handleChange} fullWidth/>
                            </div>
                            <div className={localClasses.formField}>
                                <TextInput label="Ako dlho bol pochovaný" name={'period_buried'} value={values.period_buried} onChange={handleChange} fullWidth/>
                            </div>
                            <div className={localClasses.formField}>
                                <SelectInput label="Exhumácia" name={'exhumation'} value={values.exhumation} options={yesNoOptions} onChange={handleChange} fullWidth/>
                            </div>
                        </Grid>
                    </Grid>
                </Paper>

                <div className={localClasses.formActionsBar}>
                    <Button
                        className={classes.secondaryAction}
                        onClick={() => setSuccess(true)}
                    >
                        Späť na detaily
                    </Button>
                    <Button
                        type='submit'
                        className={classes.primaryAction}
                    >
                        Uložiť zmeny
                    </Button>
                </div>
            </Container>

            <Popup
                title={'Upozornenie'}
                openPopup={show}
                setOpenPopup={setShow}
                maxW="sm"
            >
                <Typography className={localClasses.warningText}>
                    Pri úprave dát, ktoré sú uvedené pri podpísaných dokumentoch, sa podpis pri dokumente vymaže!
                </Typography>
                <Typography className={localClasses.warningSubtitle}>
                    Dáta pre Dokumenty na podpis
                </Typography>
                <Typography className={localClasses.warningBody}>
                    majiteľ pohrebného miesta, číslo hrobového miesta
                </Typography>
            </Popup>

            {success ? <Redirect to={{
                pathname: `/pohreb/${params.funeral_id}/info`,
                state: {from: props.location, number: 1}
            }}/> : <></>}
        </form>
    );
}
